import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomersService } from '../../services/customers/customers.service';

function unwrapApiResponse(data: any): any {
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) return data.data;
  return data;
}

export function useCustomers(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['customers', 'list', params],
    queryFn: async () => {
      const data = await CustomersService.list(params);
      return Array.isArray(data) ? data : [];
    },
    enabled: true,
  });
}

export function useCustomerDetails(id: string | number) {
  return useQuery({
    queryKey: ['customers', 'details', id],
    queryFn: async () => CustomersService.getById(id),
    enabled: !!id,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => CustomersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', 'list'] });
    },
  });
}

export function useCustomerDashboard(id: string | number) {
  return useQuery({
    queryKey: ['customers', 'dashboard', id],
    queryFn: async () => {
      const data = await CustomersService.getDashboard(id);
      return unwrapApiResponse(data);
    },
    enabled: !!id,
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: any }) => CustomersService.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customers', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['customers', 'details', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['customers', 'dashboard', variables.id] });
    },
  });
}
